import React from 'react';

/**
 * Internal how-to guide for the Investment Perspectives monthly process.
 * Drummond staff only — documents the publishing model and its exceptions,
 * so keep it in sync when those rules change (e.g. HOUSE_VIEW_TILT_DOMAINS
 * in the backend api.py).
 */
const HelpGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="propagate-modal-overlay" onClick={onClose}>
      <div className="propagate-modal ip-help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Investment Perspectives — How It Works</h3>
          <button className="propagate-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="ip-help-body">
          <h4>Monthly cycle</h4>
          <ol>
            <li>Select the report month (available from July 2026 onwards). All charts,
              tables and positioning automatically use data as at that month end.</li>
            <li>Author the Drummond version first: use <strong>Copy From</strong> in Edit Mode
              to pull the prior month's structure and text, then update the commentary.
              Data blocks refresh themselves — only the words need editing.</li>
            <li>For each tailored brand, select it in the sidebar and either propagate the
              Drummond content across or author it separately, then tailor the text.</li>
            <li>Open <strong>Manage White-Label</strong> and set which domains get their own
              cover branding for the month — a new month starts empty, so use its
              <strong> Copy From</strong> to carry last month's list forward.</li>
            <li>Publish via <strong>Manage Publishing</strong> when each version is ready.</li>
          </ol>
          <p className="ip-help-routine">
            In short: <strong>author Drummond &rarr; propagate/tailor per brand &rarr; tick
            white-label &rarr; publish</strong> — clients only ever see the version, months
            and branding explicitly given to them.
          </p>

          <h4>Who sees what</h4>
          <ul>
            <li><strong>Drummond group</strong> = every client domain <em>without</em> a tailored
              portfolio suite (all the mirror/branded firms). They all receive the Drummond
              version with their own branding applied. There is no stored list — any newly
              onboarded non-tailored domain is automatically part of the group.</li>
            <li><strong>Tailored clients</strong> (those granted tailored portfolios in the Domain
              Manager) only ever see their own version. If their version hasn't been saved,
              the month is hidden from them entirely — they never fall back to the
              Drummond report.</li>
            <li><strong>Cover branding</strong> is separate again: <strong>Manage White-Label</strong>
              curates, per month, which domains get their own branding on the report.
              For ticked domains, Drummond-navy shapes (like the cover boxes)
              automatically render in their primary colour, logo blocks show their
              logo and <strong>Brand Name</strong> blocks (Add Block &rarr; Text) print
              their company name in place of the Drummond wording
              — no per-report editing needed, and their palette flows through
              headings, icons, charts and tables too. White-labelled brands also
              become editable for that month: saving creates their own version,
              which overrides the Drummond mirror. Unticked domains get the
              complete Drummond look — navy throughout and the Drummond logo —
              whatever their stored branding row says. A new month starts empty —
              use the modal's Copy From to carry last month's list forward.
              Membership is a business decision — there's no automatic rule.</li>
          </ul>

          <h4>Publishing matrix</h4>
          <ul>
            <li>Rows are months (latest first); columns are <strong>Drummond</strong> plus each
              tailored brand. A ticked cell makes that month visible to that audience.</li>
            <li>The Drummond column covers all non-tailored clients in one tick.</li>
            <li>A tailored client needs <em>both</em> their cell ticked <em>and</em> their content
              saved before the month appears for them.</li>
          </ul>

          <h4>Positioning tables</h4>
          <ul>
            <li>The Asset Class Overview and Asset Class Positioning blocks are
              client-dependent: tailored clients see tilts averaged across the Portfolio
              Dashboard suite(s) ticked for them in <strong>Manage Positioning</strong> (e.g. the
              main suite, not the ETF suite) — the same result as that Dashboard tab.
              Everyone else sees the Drummond Strategic house view.</li>
            <li>From the September 2026 edition the Asset Class Overview lists Australian
              and Global Equities on separate lines (same positions as the dot table) and
              covers only equities, government bonds, corporate debt, infrastructure and
              property — Cash, Commodities and other classes are left out. Earlier months
              keep the combined Equities line.</li>
            <li>A tailored client with no suite ticked shows a "positioning suite not set"
              message in both blocks — tick one before publishing to them. The choice applies
              to every month; a report a client has already opened stays frozen (use refresh
              snapshot to rebuild it).</li>
            <li>Tilts are pinned to the report month using the approved target set in force
              at that date — a rebalance after month end won't change a published report.</li>
          </ul>

          <h4>Exceptions &amp; gotchas</h4>
          <ul>
            <li><strong>Halcyon Private Wealth</strong>: tailored for content/publishing, but by
              agreement receives the <em>Drummond house-view</em> positioning tables, not
              their own suite's.</li>
            <li><strong>NavWealth &amp; Zone Financial</strong>: suite positioning history starts
              mid-August 2026, so a July report for them would show empty positioning
              tables.</li>
            <li>Deleting a tailored month's content entirely hides it from that client — but a
              saved-yet-empty version still publishes as an empty report, so unpublish
              before clearing content.</li>
            <li>Drummond staff bypass all publish gates: you can always open and preview
              unpublished months, including tailored brands (which preview with Drummond
              content until their own version is saved — clients can't see this).</li>
          </ul>

          <h4>Word round-trip</h4>
          <ul>
            <li><strong>Download Word (text)</strong> exports the commentary as an editable file;
              <strong> Upload Word (text)</strong> loads the edited text back into the same month
              and brand. <strong>Bulk Download</strong> exports PDFs across brands.</li>
          </ul>
        </div>

        <div className="propagate-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default HelpGuideModal;
